import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { Input } from './input';
import { Badge } from './badge';
import { cn } from '../../lib/utils';

/**
 * SkillsInput - Autocomplete input with pill selection for skills
 *
 * @param {Object[]} skills - Array of skill objects { id, name, level? }
 * @param {Function} onChange - Callback(skills) when selection changes
 * @param {Object[]} availableSkills - Array of available skills from database { id, name, category? }
 * @param {boolean} allowFreeText - Allow typing custom skills (for candidates)
 * @param {string} placeholder - Input placeholder text
 * @param {string} className - Additional CSS classes
 */
const SkillsInput = ({
  skills = [],
  onChange,
  availableSkills = [],
  allowFreeText = false,
  placeholder = 'Type to search skills...',
  className
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter available skills based on search term and exclude already selected
  const filteredSkills = React.useMemo(() => {
    if (!searchTerm) return [];

    const searchLower = searchTerm.toLowerCase();
    const selectedIds = skills.map(s => s.id);

    return availableSkills
      .filter(skill =>
        !selectedIds.includes(skill.id) &&
        skill.name.toLowerCase().includes(searchLower)
      )
      .slice(0, 10); // Limit to 10 results
  }, [searchTerm, availableSkills, skills]);

  // Check if search term exactly matches an available skill
  const exactMatch = filteredSkills.find(
    skill => skill.name.toLowerCase() === searchTerm.toLowerCase()
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddSkill = (skill) => {
    onChange([...skills, { ...skill, level: 'Intermediate', source: 'manual' }]);
    setSearchTerm('');
    setShowDropdown(false);
    setFocusedIndex(-1);
    inputRef.current?.focus();
  };

  const handleRemoveSkill = (skillToRemove) => {
    onChange(skills.filter(s => s.id !== skillToRemove.id));
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
    setFocusedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown && e.key !== 'Enter') {
      setShowDropdown(true);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev =>
          prev < filteredSkills.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;

      case 'Enter':
        e.preventDefault();

        if (focusedIndex >= 0 && filteredSkills[focusedIndex]) {
          // Select focused skill from dropdown
          handleAddSkill(filteredSkills[focusedIndex]);
        } else if (exactMatch) {
          // Exact match - add it
          handleAddSkill(exactMatch);
        } else if (allowFreeText && searchTerm.trim()) {
          // Free text mode - add custom skill as pending
          const customSkill = {
            id: `pending-${Date.now()}`,
            name: searchTerm.trim(),
            isPending: true,
            source: 'manual'
          };
          handleAddSkill(customSkill);
        }
        break;

      case 'Escape':
        setShowDropdown(false);
        setFocusedIndex(-1);
        break;

      case 'Backspace':
        if (searchTerm === '' && skills.length > 0) {
          // Remove last skill when backspace on empty input
          handleRemoveSkill(skills[skills.length - 1]);
        }
        break;

      default:
        break;
    }
  };

  return (
    <div className={cn('relative', className)}>
      {/* Selected Skills Pills */}
      {skills.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {skills.map((skill) => (
            <Badge
              key={skill.id}
              variant="secondary"
              className="pl-3 pr-1 py-1 text-sm"
            >
              {skill.name}
              <button
                type="button"
                onClick={() => handleRemoveSkill(skill)}
                className="ml-1 hover:bg-black/10 rounded p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Input Field */}
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          className="w-full"
        />

        {/* Dropdown */}
        {showDropdown && (filteredSkills.length > 0 || (allowFreeText && searchTerm)) && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {filteredSkills.map((skill, index) => (
              <div
                key={skill.id}
                onClick={() => handleAddSkill(skill)}
                onMouseEnter={() => setFocusedIndex(index)}
                className={cn(
                  'px-3 py-2 cursor-pointer text-sm',
                  focusedIndex === index ? 'bg-accent' : 'hover:bg-accent'
                )}
              >
                {skill.name}
                {skill.category && (
                  <span className="text-xs text-muted-foreground ml-2">({skill.category})</span>
                )}
              </div>
            ))}

            {/* Free text option for candidates */}
            {allowFreeText && searchTerm && !exactMatch && (
              <div
                onClick={() => {
                  const customSkill = {
                    id: `pending-${Date.now()}`,
                    name: searchTerm.trim(),
                    isPending: true,
                    source: 'manual'
                  };
                  handleAddSkill(customSkill);
                }}
                className={cn(
                  'px-3 py-2 cursor-pointer text-sm border-t',
                  focusedIndex === filteredSkills.length ? 'bg-accent' : 'hover:bg-accent'
                )}
              >
                <span className="font-medium">Add "{searchTerm}"</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Helper Text */}
      {allowFreeText && (
        <p className="text-xs text-muted-foreground mt-1">
          Type to search, or add your own if not listed.
        </p>
      )}
    </div>
  );
};

export default SkillsInput;
